import React, { useCallback, useMemo } from 'react'
import { sortBy } from 'lib/array/arrayUtils'
import useFlightFareLabels from 'hooks/Flights/useFlightFareLabels'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'LuxKit/TextLink'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatCurrency from 'components/Common/FormatCurrency'
import FlightDealLabel from 'components/OfferPage/FlightDeal/FlightDealLabel'
import { formatFlightDealTravelPeriods } from 'components/OfferPage/FlightDeal/formatFlightDealTravelPeriods'

interface Props {
  originAirportCode?: string
  destinationAirportCode: string
  isFlightsActive: boolean
  showCountdown?: boolean
}

function OfferCalendarFlightDealBanner(props: Props) {
  const {
    destinationAirportCode,
    originAirportCode,
    isFlightsActive,
    showCountdown = true,
  } = props

  const { fareTypeLabelMap } = useFlightFareLabels()
  const offerPageDispatch = useOfferPageDispatch()
  const [deals] = useFlightDeals('all', {
    originAirportCode,
    destinationAirportCode,
  })

  const activeDeal = useMemo(() => {
    return sortBy(deals, (deal) => deal.perAdultPrice, 'asc')[0]
  }, [deals])

  const travelPeriodsText = useMemo(() => {
    if (!activeDeal?.travelPeriods) return ''
    return formatFlightDealTravelPeriods(activeDeal.travelPeriods)
  }, [activeDeal?.travelPeriods])

  const handleToggleFlights = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setFlights, flights: true })
  }, [offerPageDispatch])

  if (!activeDeal) {
    return null
  }

  const priceBlock = (
    <Group direction="vertical" horizontalAlign="start" gap={4} style={{ width: '100%' }}>
      <BodyText variant="medium">
        {fareTypeLabelMap[activeDeal.fareType]} from{' '}
        <strong><FormatCurrency value={activeDeal.perAdultPrice} format="roundedDollar" />/adult</strong>
      </BodyText>
      {isFlightsActive && travelPeriodsText && (
        <BodyText variant="medium" colour="neutral-two">
          Select dates <strong>{travelPeriodsText}</strong>
        </BodyText>
      )}
      {!isFlightsActive && (
        <TextLink size="small" colour="neutral-two" weight="regular" onClick={handleToggleFlights}>
          Toggle flights on to see sale dates
        </TextLink>
      )}
    </Group>
  )

  const tabletPriceBlock = (
    <Group direction="vertical" horizontalAlign="start" gap={4} style={{ width: '100%' }}>
      <BodyText variant="medium">
        {activeDeal.carrier.name} {originAirportCode}-{destinationAirportCode} {fareTypeLabelMap[activeDeal.fareType]} from{' '}
        <strong><FormatCurrency value={activeDeal.perAdultPrice} format="roundedDollar" />/adult</strong>
      </BodyText>
      {isFlightsActive && travelPeriodsText && (
        <BodyText variant="medium" colour="neutral-two">
          Travel on select dates between <strong>{travelPeriodsText}</strong>
        </BodyText>
      )}
      {!isFlightsActive && (
        <TextLink size="small" colour="neutral-two" weight="regular" onClick={handleToggleFlights}>
          Toggle flights on to see sale dates
        </TextLink>
      )}
    </Group>
  )

  return (
    <>
      <CSSBreakpoint max="mobile">
        <Group direction="vertical" horizontalAlign="stretch" gap={12} style={{ width: '100%' }}>
          <Group direction="horizontal" verticalAlign="center" gap={8}>
            <FlightDealLabel activeDeal={activeDeal} showCountdown={showCountdown} />
            {activeDeal.carrier.logo && <Image image={activeDeal.carrier.logo} height={24} />}
          </Group>
          {priceBlock}
        </Group>
      </CSSBreakpoint>

      <CSSBreakpoint min="tablet">
        <Group direction="vertical" horizontalAlign="stretch" gap={12} style={{ width: '100%' }}>
          <Group direction="horizontal" verticalAlign="center" gap={8}>
            <FlightDealLabel activeDeal={activeDeal} showCountdown={showCountdown} />
            {activeDeal.carrier.logo && <Image image={activeDeal.carrier.logo} height={24} />}
          </Group>
          {tabletPriceBlock}
        </Group>
      </CSSBreakpoint>
    </>
  )
}

export default OfferCalendarFlightDealBanner
